import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Play, Pause, Sparkles, Download } from 'lucide-react';

/*
 * HeroV2 — same working chat demo as the home page Hero,
 * restyled to the blue/indigo V2 charte. Logic is preserved 1:1.
 */
export default function HeroV2() {
  const { t, i18n } = useTranslation();
  const chatContainerRef = useRef(null);

  const handleMarketplaceClick = () => {
    window.open('https://fr.wordpress.org/plugins/askeet/', '_blank');
  };
  const handleDownloadClick = () => {
    const link = document.createElement('a');
    link.href = '/askeet.4.0.0.zip';
    link.setAttribute('download', '');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [sampleQuestions, setSampleQuestions] = useState(() => t('demo.sampleQuestions', { returnObjects: true }));

  const getBotResponse = (question) => {
    const botData = t(`demo.bot.${question}`, { returnObjects: true });
    if (!botData || !botData.tableHeaders) return null;
    return { tableHeaders: botData.tableHeaders, rows: botData.rows };
  };

  const [messages, setMessages] = useState([
    { type: 'user', content: sampleQuestions[0] },
    { type: 'bot', content: getBotResponse(sampleQuestions[0]) },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isDemoMode, setIsDemoMode] = useState(true);
  const [currentDemoIndex, setCurrentDemoIndex] = useState(0);

  useEffect(() => {
    const q = t('demo.sampleQuestions', { returnObjects: true });
    setSampleQuestions(q);
    setMessages([
      { type: 'user', content: q[0] },
      { type: 'bot', content: getBotResponse(q[0]) },
    ]);
    setCurrentDemoIndex(0);
    setInputValue('');
  }, [i18n.language]);

  const scrollToBottom = () => {
    const container = chatContainerRef.current;
    if (!container) return;
    const start = container.scrollTop;
    const distance = container.scrollHeight - start;
    const duration = 500;
    let startTime = null;
    const ease = (p) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);
    const step = (now) => {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime;
      const p = Math.min(elapsed / duration, 1);
      container.scrollTop = start + distance * ease(p);
      if (elapsed < duration) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  const handleQuestionClick = (question) => {
    const response = getBotResponse(question);
    if (response) {
      setMessages((prev) => [
        ...prev,
        { type: 'user', content: question },
        { type: 'bot', content: response },
      ]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!sampleQuestions.includes(inputValue)) {
      alert(t('demo.invalidQuestion', 'Please use one of the sample questions provided below.'));
      return;
    }
    handleQuestionClick(inputValue);
    setInputValue('');
  };

  useEffect(() => {
    let typingTimeout, sendTimeout, nextTimeout;
    if (isDemoMode) {
      setInputValue('');
      setMessages([
        { type: 'user', content: sampleQuestions[currentDemoIndex] },
        { type: 'bot', content: getBotResponse(sampleQuestions[currentDemoIndex]) },
      ]);
      let i = 0;
      const typeChar = () => {
        setInputValue(sampleQuestions[currentDemoIndex].slice(0, i));
        if (i < sampleQuestions[currentDemoIndex].length) {
          i++;
          typingTimeout = setTimeout(typeChar, 50);
        } else {
          sendTimeout = setTimeout(() => {
            setMessages((prev) => [
              ...prev,
              { type: 'user', content: sampleQuestions[currentDemoIndex] },
              { type: 'bot', content: getBotResponse(sampleQuestions[currentDemoIndex]) },
            ]);
            setInputValue('');
            nextTimeout = setTimeout(() => {
              setCurrentDemoIndex((currentDemoIndex + 1) % sampleQuestions.length);
            }, 2000);
          }, 600);
        }
      };
      typeChar();
    }
    return () => {
      clearTimeout(typingTimeout);
      clearTimeout(sendTimeout);
      clearTimeout(nextTimeout);
    };
  }, [isDemoMode, currentDemoIndex, sampleQuestions, i18n.language]);

  return (
    <header className="v2-hero">
      <div className="v2-container v2-hero-grid">
        {/* Left column */}
        <div className="v2-hero-copy">
          <span className="v2-badge"><span className="dot" /> {t('domains.badge')}</span>
          <h1>
            <span className="l1">{t('hero.title_top_part1')}</span>
            <span className="l2 v2-grad-text">{t('hero.title_top_part2')}</span>
          </h1>
          <div className="v2-hero-line2">
            {t('hero.title_part1')} <span className="v2-grad-text">{t('hero.title_part2')}</span>
          </div>
          <p className="v2-hero-sub">{t('hero.subtitle')}</p>
          <p className="v2-hero-desc">{t('hero.description')}</p>
          <div className="v2-hero-actions">
            <button className="v2-btn v2-btn-primary v2-btn-lg" onClick={handleMarketplaceClick}>
              {t('hero.cta')} <ArrowRight />
            </button>
            <button className="v2-btn v2-btn-ghost v2-btn-lg" onClick={handleDownloadClick}>
              <Download /> {t('hero.download_plugin')}
            </button>
          </div>
        </div>

        {/* Right column — chat mockup */}
        <div className="v2-mockup">
          <div className="v2h-frame">
            <div className="v2h-window">
              <div className="v2h-bar">
                <div className="left">
                  <div className="v2h-dots"><span /><span /><span /></div>
                  <span className="v2h-title">Askeet Chat</span>
                </div>
                <button className="v2h-demoToggle" onClick={() => setIsDemoMode((v) => !v)}>
                  {isDemoMode ? (<><Pause size={14} /> Pause Demo</>) : (<><Play size={14} /> Start Demo</>)}
                </button>
              </div>

              <div className="v2h-body">
                <div className="v2h-chat" ref={chatContainerRef}>
                  {messages.map((message, index) => (
                    <div key={index} className={`v2h-row ${message.type}`}>
                      {message.type === 'user' ? (
                        <div className="v2h-user">{message.content}</div>
                      ) : (
                        message.content && message.content.tableHeaders && message.content.rows ? (
                          <div className="v2h-bot">
                            <div className="v2h-bot-head">
                              <span className="v2h-bot-title">
                                <Sparkles size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />
                                {t('demo.bot.responseTitle')}
                              </span>
                              <button className="v2h-export" onClick={() => alert('Export functionality coming soon!')}>
                                <Download />
                                {t('demo.bot.export')}
                              </button>
                            </div>
                            <table className="v2h-table">
                              <thead>
                                <tr>
                                  {message.content.tableHeaders.map((h, i) => <th key={i}>{h}</th>)}
                                </tr>
                              </thead>
                              <tbody>
                                {message.content.rows.map((row, i) => (
                                  <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : null
                      )}
                    </div>
                  ))}
                </div>

                <p className="v2h-tryLabel">{t('demo.tryAsking', 'Try asking:')}</p>
                <div className="v2h-chips">
                  {sampleQuestions.map((question, index) => (
                    <button
                      key={index}
                      className={`v2h-chip ${isDemoMode && currentDemoIndex === index ? 'active' : ''}`}
                      onClick={() => handleQuestionClick(question)}
                    >
                      {question}
                    </button>
                  ))}
                </div>

                <form className="v2h-form" onSubmit={handleSubmit}>
                  <input
                    className="v2h-input"
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={t('demo.placeholder')}
                  />
                  <button type="submit" className="v2h-send" aria-label="Send"><ArrowRight size={18} /></button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
